import {
	IconArchive,
	IconBrandGithub,
	IconCode,
	IconWorld,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { allProjects } from "content-collections";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { config } from "@/data/config";
import { cn } from "@/lib/utils";
import { schema, toScript } from "@/utils/schema";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/_pages/projects")({
	component: RouteComponent,
	head: () => {
		const description =
			"A list of open source and personal projects I currently maintain or have built over the years.";
		const base = seo({
			title: `Projects | ${config.Name}`,
			description,
			keywords:
				"projects, open source, software engineering, web development, tools, side projects",
			url: `${config.Url}/projects`,
		});
		return {
			...base,
			scripts: [
				...base.scripts,
				toScript(
					schema.collectionPage(
						`${config.Name} — Projects`,
						`${config.Url}/projects`,
						description,
					),
				),
				toScript(
					schema.breadcrumb([
						{ name: config.Name, url: config.Url },
						{ name: "Projects", url: `${config.Url}/projects` },
					]),
				),
			],
		};
	},
});

function RouteComponent() {
	const activeProjects = allProjects.filter(
		(project) => project.active && project.visible,
	);
	const inactiveProjects = allProjects.filter(
		(project) => !project.active && project.visible,
	);

	return (
		<div className="relative bg-background">
			<div
				className="pointer-events-none absolute inset-0 surface-grid opacity-50"
				aria-hidden
			/>
			<main className="relative pt-12 sm:pt-20">
				<div className="mx-auto max-w-6xl px-4 lg:px-8">
					<div className="animate-fade-in-up">
						<MarketingHeader
							heading="Projects"
							description="See my list of projects, tools and other things that I currently maintain or previously maintained."
						/>
					</div>

					<div className="pb-20">
						<section className="mb-16 last:mb-0">
							<div
								className="mb-8 flex animate-in items-center gap-3 delay-200 duration-800 fill-mode-both fade-in slide-in-from-bottom-8"
								style={{ animationDelay: "0.2s" }}
							>
								<div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
									<IconCode className="size-5" />
								</div>
								<div>
									<h2 className="text-3xl font-semibold tracking-tight text-foreground">
										Active projects
									</h2>
									<p className="mt-1 text-sm text-muted-foreground">
										<span className="tabular-nums">
											{activeProjects.length}
										</span>{" "}
										{activeProjects.length === 1 ? "project" : "projects"}
									</p>
								</div>
							</div>
							{activeProjects.length > 0 ? (
								<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
									{activeProjects.map((project, index) => (
										<div
											key={project.title}
											className="min-h-0 min-w-0 animate-in delay-400 duration-800 fill-mode-both fade-in slide-in-from-bottom-8"
											style={{ animationDelay: `${0.4 + index * 0.1}s` }}
										>
											<Card
												className={cn(
													"group relative h-full overflow-hidden border-border/80 bg-card shadow-lg transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-xl dark:shadow-none dark:hover:shadow-none",
													project.featured &&
														"ring-2 ring-primary/50 ring-offset-2 ring-offset-background dark:ring-primary/40 dark:ring-offset-background",
												)}
											>
												<div className="pointer-events-none absolute inset-0 bg-linear-to-br from-primary/6 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:from-primary/4" />
												<CardHeader className="relative gap-0">
													<div className="mb-3 flex items-start justify-between gap-3">
														<CardTitle className="text-xl font-semibold text-foreground transition-colors group-hover:text-primary">
															{project.title}
														</CardTitle>
														<Badge
															variant={
																project.openSource ? "default" : "outline"
															}
															className="shrink-0"
														>
															{project.openSource
																? "Open source"
																: "Closed source"}
														</Badge>
													</div>
													<ul className="mb-3 flex flex-wrap gap-1.5">
														{project.technologies.map((tech) => (
															<li key={tech}>
																<Badge
																	variant="secondary"
																	className="font-normal"
																>
																	{tech}
																</Badge>
															</li>
														))}
													</ul>
													<CardDescription className="text-base text-pretty text-muted-foreground">
														{project.description}
													</CardDescription>
												</CardHeader>
												<CardFooter className="relative mt-auto flex gap-2 pt-2">
													{project.website ? (
														<Button
															variant="outline"
															size="sm"
															asChild
															className="flex-1 rounded-lg border-border/80 bg-background/50"
														>
															<a
																href={project.website}
																target="_blank"
																rel="noopener noreferrer"
																className="flex items-center justify-center gap-2"
															>
																<IconWorld className="size-3.5" />
																Website
															</a>
														</Button>
													) : null}
													{project.githubUrl ? (
														<Button
															variant="outline"
															size="sm"
															asChild
															className="flex-1 rounded-lg border-border/80 bg-background/50"
														>
															<a
																href={project.githubUrl}
																target="_blank"
																rel="noopener noreferrer"
																className="flex items-center justify-center gap-2"
															>
																<IconBrandGithub className="size-3.5" />
																Source
															</a>
														</Button>
													) : null}
												</CardFooter>
											</Card>
										</div>
									))}
								</div>
							) : (
								<div
									className="animate-in rounded-xl border border-dashed border-border/80 bg-muted/10 py-14 text-center delay-400 duration-800 fill-mode-both fade-in slide-in-from-bottom-8"
									style={{ animationDelay: "0.4s" }}
								>
									<p className="text-muted-foreground">
										No active projects to show.
									</p>
								</div>
							)}
						</section>

						{inactiveProjects.length > 0 ? (
							<section>
								<div
									className="mb-8 flex animate-in items-center gap-3 delay-200 duration-800 fill-mode-both fade-in slide-in-from-bottom-8"
									style={{
										animationDelay: `${0.35 + Math.min(activeProjects.length, 12) * 0.06}s`,
									}}
								>
									<div className="flex size-10 items-center justify-center rounded-xl bg-muted text-muted-foreground">
										<IconArchive className="size-5" />
									</div>
									<div>
										<h2 className="text-3xl font-semibold tracking-tight text-foreground">
											Archived projects
										</h2>
										<p className="mt-1 text-sm text-muted-foreground">
											<span className="tabular-nums">
												{inactiveProjects.length}
											</span>{" "}
											{inactiveProjects.length === 1 ? "project" : "projects"}
										</p>
									</div>
								</div>
								<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
									{inactiveProjects.map((project, index) => (
										<div
											key={project.title}
											className="min-h-0 min-w-0 animate-in delay-400 duration-800 fill-mode-both fade-in slide-in-from-bottom-8"
											style={{
												animationDelay: `${0.55 + activeProjects.length * 0.08 + index * 0.1}s`,
											}}
										>
											<Card className="h-full border-border/60 bg-muted/20 opacity-90 transition-opacity duration-300 hover:opacity-100 dark:border-border/80 dark:bg-muted/10">
												<CardHeader className="gap-0">
													<div className="mb-3 flex items-start justify-between gap-3">
														<CardTitle className="text-lg font-semibold text-muted-foreground">
															{project.title}
														</CardTitle>
														<Badge
															variant={
																project.openSource ? "secondary" : "outline"
															}
															className="shrink-0"
														>
															{project.openSource
																? "Open source"
																: "Closed source"}
														</Badge>
													</div>
													<ul className="mb-3 flex flex-wrap gap-1.5">
														{project.technologies.map((tech) => (
															<li key={tech}>
																<Badge
																	variant="outline"
																	className="font-normal opacity-80"
																>
																	{tech}
																</Badge>
															</li>
														))}
													</ul>
													<CardDescription className="text-base text-pretty text-muted-foreground">
														{project.description}
													</CardDescription>
												</CardHeader>
												<CardFooter className="mt-auto flex gap-2 pt-2">
													{project.website ? (
														<Button
															variant="ghost"
															size="sm"
															asChild
															className="flex-1"
														>
															<a
																href={project.website}
																target="_blank"
																rel="noopener noreferrer"
																className="flex items-center justify-center gap-2"
															>
																<IconWorld className="size-3.5" />
																Website
															</a>
														</Button>
													) : null}
													{project.githubUrl ? (
														<Button
															variant="ghost"
															size="sm"
															asChild
															className="flex-1"
														>
															<a
																href={project.githubUrl}
																target="_blank"
																rel="noopener noreferrer"
																className="flex items-center justify-center gap-2"
															>
																<IconBrandGithub className="size-3.5" />
																Source
															</a>
														</Button>
													) : null}
												</CardFooter>
											</Card>
										</div>
									))}
								</div>
							</section>
						) : null}
					</div>
				</div>
			</main>
		</div>
	);
}
